import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { CookiePolicy } from '@ott/cookie-policy';
import { Container } from '@ott/grid';
import { authActions } from '@ott/solar-auth';
import Footer from '@ott/solar-footer';
import Header, { HEADER_THEME_DARK } from '@ott/solar-header';

import { getAuthSelector } from 'src/redux/selectors/commonSelectors';
import { RootState } from 'src/redux/store';

import MobileAppBanner from 'src/components/blocks/MobileAppBanner';
import SupportChat from 'src/components/blocks/SupportChat';

import { COOKIE_POLICY_LINK } from 'src/constants/common';

import useIsWebview from 'src/hooks/useIsWebview';
import useTypedSelector from 'src/hooks/useTypedSelector';

import Earn from './blocks/Earn';
import FAQ from './blocks/FAQ';
import Intro from './blocks/Intro';
import Loader from './blocks/Intro/Loader';
import Invite from './blocks/Invite';

import { motion, useAnimation } from 'framer-motion';

import styles from './ReferralPage.scss';

const ReferralPage = () => {
  const [totalElements, setTotalElements] = useState(-1);
  const [currentlyLoaded, setCurrentlyLoaded] = useState(0);
  const [loader, setLoader] = useState(true);
  const [displayContent, setDisplayContent] = useState(false);

  const ssr: boolean = useTypedSelector((state: RootState) => state.common.ssr);
  const auth = useTypedSelector(getAuthSelector);
  const isWebview = useIsWebview();
  const dispatch = useDispatch();
  const control = useAnimation();
  const [direction, setDirection] = useState<boolean>(true);
  const [isAnimating, setIsAnimating] = useState(false);

  const history = useHistory();

  const isAuthorized = auth?.data?.auth;

  const [newMessages, setNewMessages] = useState(0);
  const [isSupportChatVisible, setIsSupportChatVisible] = useState(false);
  const [isSupportChatOpen, setIsSupportChatOpen] = useState(false);

  const handleLoadEvent = () => {
    setCurrentlyLoaded((prevState) => prevState + 1);
  };

  useEffect(() => {
    if (auth.infoStatus.initial) {
      dispatch(authActions.getBasicUserInfo());
    }
  }, []);

  useEffect(() => {
    if (!ssr) {
      const parent = document.getElementById('app') as HTMLDivElement;
      const children = parent.querySelectorAll('svg');

      if (totalElements === -1) {
        setTotalElements(children.length);
      }

      for (let i = 0; i <= children.length - 1; i++) {
        children[i].addEventListener('load', handleLoadEvent);
      }
    }
  }, [ssr]);

  useEffect(() => {
    if (totalElements === currentlyLoaded) {
      setTimeout(() => setLoader(false), 3000);
    }
  }, [totalElements, currentlyLoaded]);

  useEffect(() => {
    const scrolling = () => {
      if (!isAnimating && window.scrollY > 5 && window.scrollY < 500 && direction) {
        control.start('animate');
        setIsAnimating(true);
        setDisplayContent(true);
      }
      if (!isAnimating && window.scrollY <= 0 && !direction) {
        control.start('initial');
        setIsAnimating(true);
        setDisplayContent(false);
      }
      if (!isAnimating && window.scrollY >= 500) {
        control.set('animate');
        setDirection(false);
        setDisplayContent(true);
      }
    };
    window.addEventListener('scroll', scrolling);
    return () => window.removeEventListener('scroll', scrolling);
  }, [direction, isAnimating, control]);

  const headerVariants = {
    initial: {
      y: 0,
      transition: {
        delay: 2,
        duration: 1,
      },
    },
    animate: {
      y: -100,
      transition: {
        duration: 1,
      },
    },
  };

  const handleNewMessages = useCallback((count: number) => {
    setNewMessages(count);
  }, []);

  const handleOpenSupportChat = useCallback((isOpen: boolean) => {
    setIsSupportChatOpen(isOpen);
  }, []);

  const onSupportChatVisible = useCallback((isVisible: boolean) => {
    setIsSupportChatVisible(isVisible);
  }, []);

  if (ssr) {
    return null;
  }

  if (loader && !isWebview) {
    return <Loader visible={totalElements !== currentlyLoaded} />;
  }

  return (
    <div className={styles.container}>
      {!isWebview && (
        <>
          <CookiePolicy cookieLink={COOKIE_POLICY_LINK} />
          <motion.div variants={headerVariants} animate={control} className={styles.header}>
            <Header
              theme={HEADER_THEME_DARK}
              history={history}
              isTransparent={true}
              supportNewMessagesCount={newMessages}
              onOpenSupportChat={handleOpenSupportChat}
              isSupportChatVisible={isSupportChatVisible}
            />
          </motion.div>
        </>
      )}
      <Container className={styles.content}>
        <Intro
          setDisplayContent={(e: boolean) => setDisplayContent(e)}
          control={control}
          isAnimating={isAnimating}
          setIsAnimating={(e: boolean) => setIsAnimating(e)}
          direction={direction}
          setDirection={(e: boolean) => setDirection(e)}
        />
        <div className={displayContent ? styles.showContent : styles.noContent}>
          <Earn />
          <Invite />
          {!isWebview && <MobileAppBanner picture="planet" />}
          <FAQ />
        </div>
      </Container>
      {!isWebview && <Footer className={displayContent ? styles.showContent : styles.noContent} />}
      <SupportChat
        isUserAuthorized={isAuthorized}
        isSolar={true}
        isChatButtonHidden={true}
        isOpenFromOutside={isSupportChatOpen}
        onOpenFromOutside={handleOpenSupportChat}
        onReceiveNewMessages={handleNewMessages}
        onCheckAvailability={onSupportChatVisible}
      />
    </div>
  );
};

export default ReferralPage;
